import { DEFAULT_PATIENT_PAGE_SIZE } from "@/features/patients/patientPageSize";
import { effectivePatientFilters } from "@/features/patients/patientFilters";
import type { Json } from "@/types/database/database";
import { supabaseClient } from "@/lib/supabase";
import type { PatientsSearchT } from "@/routes/_app/patients";
import type { DiariesCountT, DiaryViewT, StateKeyT, StatefulDiaryT } from "@patient-watch/types";
import type { UuidT } from "@patient-watch/types";
import type { Auth } from "@/utils/auth";
import { validate as validateUUID } from "uuid";
import { DEFAULT_LOCALE, type SupportedLocale } from "@patient-watch/i18n";
import { localizeDiaryTrackerForms } from "./localizeDiaryTrackerForms";

export async function getDiaryById(
    diaryId: string,
    locale: SupportedLocale = DEFAULT_LOCALE
): Promise<StatefulDiaryT | null> {
    if (!validateUUID(diaryId)) {
        console.warn(`Invalid UUID provided to getDiaryById: ${diaryId}`);
        return null;
    }

    const { data, error } = await supabaseClient.functions.invoke("public-get-diary-by-id", {
        body: { diary_id: diaryId },
    });

    if (error) {
        console.error(`Error fetching diary ${diaryId}:`, error);
        throw error;
    }

    if (!data) {
        console.error("No data returned from getDiaryById");
        throw new Error("No data returned from getDiaryById");
    }

    const diary = (data?.[0] as StatefulDiaryT | undefined) ?? null;
    if (!diary) {
        return null;
    }

    return localizeDiaryTrackerForms(diary, locale);
}

export async function getPatientDiaries(patientId: UuidT): Promise<StatefulDiaryT[]> {
    if (!validateUUID(patientId)) return [];

    const { data, error } = await supabaseClient.functions.invoke("public-get-patient-diaries");

    if (error) {
        console.error(`Error fetching patient diaries for ${patientId}:`, error);
        throw error;
    }

    return ((data ?? []) as StatefulDiaryT[]).filter((diary) => diary.diary_type !== PUBLIC_FORM_DIARY_TYPE);
}

// ---------------------------------------------------------------------------
// Patients-page filter helpers
// ---------------------------------------------------------------------------

/** Public form diaries are excluded from the patients table and search. */
const PUBLIC_FORM_DIARY_TYPE = "PUBLIC_FORM" as const;

interface PatientsFilters {
    /** When set, restrict to diaries directly shared with this user only
     *  (direct_access). Derived from diaries === "direct". */
    directAccessOnlyUserId?: string;
    /** When set, restrict to diaries whose direct_access overlaps with these
     *  user IDs. Comes from the "Filter by Diary Access" multi-select. */
    diaryAccessUserIds?: string[];
    /** When set, restrict to diaries in these organisation IDs.
     *  Comes from the "Filter by Organisation" multi-select. */
    organisationIds?: string[];
    /** When set, restrict to diaries whose diary_labels overlap with these
     *  label names. Comes from the "Labels" multi-select. */
    labelNames?: string[];
    /** When set, restrict to diaries whose diary_status is one of these keys. */
    diaryStatuses?: StateKeyT[];
}

/** Narrow structural type for the PostgREST filter segment used after `.select()`. */
interface DiariesViewFilterChain {
    contains: (column: string, value: string[]) => DiariesViewFilterChain;
    overlaps: (column: string, value: string[]) => DiariesViewFilterChain;
    in: (column: string, values: string[]) => DiariesViewFilterChain;
    neq: (column: string, value: string) => DiariesViewFilterChain;
}

/**
 * Apply access + organisation filters to a diaries_view query.
 *
 * diaries_view is already RLS-protected (users only see rows they can access),
 * but these filters let the user narrow further via the Table Menu settings.
 *
 * Typed as generic `T` because Supabase's PostgREST chain narrows per `.select()`
 * shape; filter helpers are not exposed on the pre-select builder type.
 */
function applyPatientsFilters<T>(query: T, filters: PatientsFilters): T {
    console.log("👀 [applyPatientsFilters] filters:", JSON.stringify(filters));

    let q = query as unknown as DiariesViewFilterChain;

    if (filters.directAccessOnlyUserId) {
        q = q.contains("direct_access", [filters.directAccessOnlyUserId]);
    }
    if (filters.diaryAccessUserIds && filters.diaryAccessUserIds.length > 0) {
        console.log("👀 [applyPatientsFilters] applying diary_access overlaps:", filters.diaryAccessUserIds);
        q = q.overlaps("direct_access", filters.diaryAccessUserIds);
    }
    const orgs = filters.organisationIds;
    if (orgs && orgs.length > 0 && !orgs.every((id) => id === "all")) {
        const validOrgIds = orgs.filter((id) => id !== "all");
        if (validOrgIds.length > 0) {
            console.log("👀 [applyPatientsFilters] applying organisation filter:", validOrgIds);
            q = q.in("organisation_id", validOrgIds);
        }
    }
    if (filters.labelNames && filters.labelNames.length > 0) {
        console.log("👀 [applyPatientsFilters] applying label filter:", filters.labelNames);
        q = q.overlaps("diary_labels", filters.labelNames);
    }
    if (filters.diaryStatuses && filters.diaryStatuses.length > 0) {
        q = q.in("diary_status", filters.diaryStatuses);
    }

    q = q.neq("diary_type", PUBLIC_FORM_DIARY_TYPE);

    return q as unknown as T;
}

export async function getDiariesTabs(filters: Record<string, unknown> = {}): Promise<{ tab: string | null }[]> {
    let query = supabaseClient.from("diaries_view").select("tab");
    query = applyPatientsFilters(query, {
        directAccessOnlyUserId: filters.user_id as string | undefined,
        diaryAccessUserIds: filters.diary_access as string[] | undefined,
        organisationIds: filters.organisations as string[] | undefined,
    });

    const { data, error } = await query.returns<{ tab: string | null }[]>();

    if (error) {
        console.error("Error fetching diary tabs:", error);
        throw error;
    }

    return data || [];
}

/**
 * Derive the filter values from the patients-page settings + auth context.
 *
 * - `directAccessOnlyUserId` is set when the user chose "Direct" in the Table Menu.
 * - `diaryAccessUserIds` is set when specific users are chosen in "Filter by Diary Access".
 * - `organisationIds` is set when specific orgs are chosen.
 */
export const buildPatientsFilters = (settings: PatientsSearchT, auth?: Auth): PatientsFilters & { tab: string } => {
    if (!settings?.tab) throw new Error("Tab is required");
    if (!auth) throw new Error("Not logged in");

    // Admins see all diaries via a separate RLS policy, but they are NOT
    // listed in the direct_access / same_organisation arrays.
    // So the "direct" filter must be skipped for admins.
    const isAdmin = auth.roles?.isAdmin === true;
    const wantsDirect = settings.diaries === "direct" && !isAdmin;

    const filters = {
        tab: settings.tab,
        directAccessOnlyUserId: wantsDirect ? auth.id : undefined,
        diaryAccessUserIds: settings.diary_access ?? undefined,
        organisationIds: settings.organisations ?? undefined,
        labelNames: settings.labels && settings.labels.length > 0 ? settings.labels : undefined,
        diaryStatuses:
            settings.diary_statuses && settings.diary_statuses.length > 0 ? settings.diary_statuses : undefined,
    };

    console.log("👀 [buildPatientsFilters] settings.diaries:", settings.diaries, "isAdmin:", isAdmin);
    console.log("👀 [buildPatientsFilters] auth.id:", auth.id);
    console.log("👀 [buildPatientsFilters] result:", JSON.stringify(filters));

    return filters;
};

export async function getAllDiaries(): Promise<DiaryViewT[]> {
    const { data, error } = await supabaseClient.from("diaries_view").select("*");

    if (error) throw error;

    return data as DiaryViewT[];
}

/** Flat search data for the patient search dialog. */
export interface DiarySearchDataT {
    diary_id: string;
    patient_name: string;
    patient_email: string;
    date_of_birth: string | null;
    hospital_number: string | null;
    tab: string | null;
}

const PATIENT_SEARCH_MIN_LENGTH = 2;
const PATIENT_SEARCH_LIMIT = 50;

/** Escape % and _ for ILIKE patterns, and strip chars that break PostgREST or=(...) syntax. */
function sanitisePatientSearch(query: string): string {
    return query.replace(/[,()]/g, " ").trim();
}

/**
 * Server-side patient search for the patients page dialog.
 * Uses search_patient_diaries() so we never scan the heavy diaries_view.
 */
export async function searchDiariesForPatientDialog(query: string): Promise<DiarySearchDataT[]> {
    const trimmed = sanitisePatientSearch(query);
    if (trimmed.length < PATIENT_SEARCH_MIN_LENGTH) {
        return [];
    }

    const { data, error } = await supabaseClient.rpc("search_patient_diaries", {
        p_query: trimmed,
        p_limit: PATIENT_SEARCH_LIMIT,
    });

    if (error) throw error;

    return (data ?? []).map((row) => ({
        diary_id: row.diary_id || "",
        patient_name: row.patient_name || "",
        patient_email: row.patient_email || "",
        date_of_birth: row.date_of_birth || null,
        hospital_number: row.hospital_number || null,
        tab: row.tab || null,
    }));
}

/**
 * Map table column IDs (from TanStack Table / Columns.tsx) to the actual
 * diaries_view column names used by PostgREST `.order()`.
 *
 * Columns not in this map can't be sorted server-side.
 */
const SORT_COLUMN_MAP: Record<string, string> = {
    // Legacy URL id from before the read column was renamed to is_read
    select: "is_read",
    is_read: "is_read",
    diary_status: "diary_status",
    name: "patient_info->>patient_name",
    first_name: "patient_first_name",
    last_name: "patient_last_name",
    hospital_number: "hospital_number",
    start_date: "start_date",
    date_of_birth: "patient_info->>date_of_birth",
    diary_name: "diary_name",
    organisation_name: "organisation_name",
    has_incomplete_events: "has_incomplete_events",
    diary_labels: "diary_labels",
};

export interface DiariesBySettingsResult {
    diaries: DiaryViewT[];
    /** Exact total count for the current tab (from Postgres, not JS). */
    currentTabCount: number;
    /** Exact counts for all tabs from the same RPC as the page rows. */
    tabCounts: DiariesCountT;
}

interface ListPatientDiariesRow {
    diary_id: string;
    diary_name: string | null;
    state: DiaryViewT["state"];
    diary_status: DiaryViewT["diary_status"];
    is_read: boolean | null;
    organisation_name: string | null;
    patient_info: DiaryViewT["patient_info"];
    hospital_number: string | null;
    tab: DiaryViewT["tab"];
    start_date: string | null;
    end_date: string | null;
    diary_labels: string[] | null;
    has_incomplete_events: boolean | null;
    total_count: number;
    active_count: number;
    pending_count: number;
    inactive_count: number;
    organisation_id: string | null;
}

/** One builder for rows, tab counts, status options, and select-all, so they always agree. */
function buildPatientsListFilterRpcParams(settings: PatientsSearchT, auth: Auth) {
    const { tab, directAccessOnlyUserId, diaryAccessUserIds, organisationIds, diaryStatuses } = buildPatientsFilters(
        settings,
        auth
    );
    // Legacy `labels` travel inside p_filters too, so SQL applies the same label-grant check.
    const filters = effectivePatientFilters(settings);

    const validOrgIds =
        organisationIds && organisationIds.length > 0 && !organisationIds.every((id) => id === "all")
            ? organisationIds.filter((id) => id !== "all")
            : null;

    return {
        p_tab: tab.toLowerCase(),
        p_direct_only_user_id: directAccessOnlyUserId ?? null,
        p_diary_access_user_ids: diaryAccessUserIds && diaryAccessUserIds.length > 0 ? diaryAccessUserIds : null,
        p_organisation_ids: validOrgIds && validOrgIds.length > 0 ? validOrgIds : null,
        p_label_names: null,
        p_filters: filters.length > 0 ? (filters as unknown as Json) : null,
        ...(diaryStatuses && diaryStatuses.length > 0 ? { p_diary_statuses: diaryStatuses } : {}),
    };
}

function buildPatientsStatusFilterRpcParams(settings: PatientsSearchT, auth: Auth) {
    const { p_diary_statuses: _pDiaryStatuses, ...params } = buildPatientsListFilterRpcParams(settings, auth);
    return params;
}

async function getPatientDiaryStatusesFromView(settings: PatientsSearchT, auth: Auth): Promise<StateKeyT[]> {
    const filters = buildPatientsFilters(settings, auth);
    let query = supabaseClient.from("diaries_view").select("diary_status");
    query = applyPatientsFilters(query, {
        directAccessOnlyUserId: filters.directAccessOnlyUserId,
        diaryAccessUserIds: filters.diaryAccessUserIds,
        organisationIds: filters.organisationIds,
        labelNames: filters.labelNames,
    });
    query = query.eq("tab", filters.tab);

    const { data, error } = await query.returns<{ diary_status: StateKeyT | null }[]>();
    if (error) throw error;

    return [...new Set((data ?? []).map((row) => row.diary_status).filter((status): status is StateKeyT => !!status))];
}

/** Distinct diary_status values for the current patients-table filters (ignores status filter). */
export async function getPatientDiaryStatuses(settings: PatientsSearchT, auth: Auth): Promise<StateKeyT[]> {
    const params = buildPatientsStatusFilterRpcParams(settings, auth);
    const { data, error } = await supabaseClient.rpc("list_patient_diary_statuses", params);

    if (!error) {
        return (data ?? [])
            .map((row) => row.diary_status)
            .filter((status): status is StateKeyT => typeof status === "string" && status.length > 0);
    }

    const missingRpc =
        error.code === "PGRST202" ||
        error.message?.toLowerCase().includes("list_patient_diary_statuses") ||
        error.message?.toLowerCase().includes("could not find the function");

    if (!missingRpc) {
        console.error("🚨 [getPatientDiaryStatuses] error:", error);
        throw error;
    }

    return getPatientDiaryStatusesFromView(settings, auth);
}

function buildListPatientDiariesRpcParams(settings: PatientsSearchT, auth: Auth) {
    const pagination = settings.pagination ?? { pageIndex: 0, pageSize: DEFAULT_PATIENT_PAGE_SIZE };
    const primarySort = settings.sorting?.[0] ?? { id: "start_date", desc: true };
    const sortColumn = SORT_COLUMN_MAP[primarySort.id] ?? "start_date";

    return {
        ...buildPatientsListFilterRpcParams(settings, auth),
        p_sort_column: sortColumn,
        p_sort_desc: primarySort.desc,
        p_offset: pagination.pageIndex * pagination.pageSize,
        p_limit: pagination.pageSize,
    };
}

function mapListPatientDiariesRows(rows: ListPatientDiariesRow[]): DiariesBySettingsResult {
    const countRow = rows[0];
    const tabCounts: DiariesCountT = {
        active: countRow?.active_count ?? 0,
        pending: countRow?.pending_count ?? 0,
        inactive: countRow?.inactive_count ?? 0,
    };
    const currentTabCount = countRow?.total_count ?? 0;

    const diaries = rows
        .filter((row): row is ListPatientDiariesRow & { diary_id: string } => row.diary_id !== null)
        .map(
            ({
                total_count: _total_count,
                active_count: _active_count,
                pending_count: _pending_count,
                inactive_count: _inactive_count,
                ...diary
            }) => diary
        );

    return {
        diaries: diaries as DiaryViewT[],
        currentTabCount,
        tabCounts,
    };
}

/**
 * Fetch a single page of diaries for the patients table via list_patient_diaries().
 * Returns rows, the current-tab total, and all tab badge counts in one round trip.
 */
export async function getDiariesBySettings(settings: PatientsSearchT, auth: Auth): Promise<DiariesBySettingsResult> {
    const params = buildListPatientDiariesRpcParams(settings, auth);

    console.log("👀 [getDiariesBySettings] tab:", params.p_tab);
    console.log("👀 [getDiariesBySettings] pagination:", JSON.stringify(settings.pagination));

    const { data, error } = await supabaseClient.rpc("list_patient_diaries", params);

    if (error) {
        console.error("🚨 [getDiariesBySettings] error:", error);
        throw error;
    }

    const result = mapListPatientDiariesRows((data ?? []) as ListPatientDiariesRow[]);

    console.log(
        "👀 [getDiariesBySettings] returned rows:",
        result.diaries.length,
        "total count:",
        result.currentTabCount
    );
    console.log("👀 [getDiariesBySettings] tab counts:", JSON.stringify(result.tabCounts));
    console.log(
        "👀 [getDiariesBySettings] diary_ids:",
        result.diaries.map((d) => d.diary_id)
    );

    return result;
}

/** @deprecated Tab counts are returned by getDiariesBySettings; kept for query-key compatibility. */
export async function getOtherTabCounts(settings: PatientsSearchT, auth: Auth): Promise<DiariesCountT> {
    const { tabCounts } = await getDiariesBySettings(settings, auth);
    return tabCounts;
}

async function getPatientDiaryIdsByPaging(settings: PatientsSearchT, auth: Auth): Promise<string[]> {
    const pageSize = 100;
    const ids: string[] = [];
    let pageIndex = 0;
    let totalCount = Number.POSITIVE_INFINITY;

    while (ids.length < totalCount) {
        const page = await getDiariesBySettings(
            {
                ...settings,
                pagination: { pageIndex, pageSize },
            },
            auth
        );
        totalCount = page.currentTabCount;
        for (const diary of page.diaries) {
            if (diary.diary_id) ids.push(diary.diary_id);
        }
        if (page.diaries.length === 0) break;
        pageIndex += 1;
        // Safety: list_patient_diaries caps at 500 per page
        if (pageIndex > 500) break;
    }

    return ids;
}

/**
 * All diary IDs matching the current patients-table filters (cross-page select-all).
 * Prefers list_patient_diary_ids; falls back to paging list_patient_diaries when the RPC is unavailable.
 */
export async function getPatientDiaryIdsBySettings(settings: PatientsSearchT, auth: Auth): Promise<string[]> {
    const params = buildPatientsListFilterRpcParams(settings, auth);
    const { data, error } = await supabaseClient.rpc("list_patient_diary_ids", params);

    if (!error) {
        return (data ?? [])
            .map((row) => row.diary_id)
            .filter((id): id is string => typeof id === "string" && id.length > 0);
    }

    const missingRpc =
        error.code === "PGRST202" ||
        error.message?.toLowerCase().includes("list_patient_diary_ids") ||
        error.message?.toLowerCase().includes("could not find the function");

    if (!missingRpc) {
        console.error("🚨 [getPatientDiaryIdsBySettings] error:", error);
        throw error;
    }

    console.warn("⚠️ [getPatientDiaryIdsBySettings] RPC missing - paging list_patient_diaries");
    return getPatientDiaryIdsByPaging(settings, auth);
}
